import { ArrowUpRightIcon, PlayIcon } from 'lucide-react';
import { HeadingText } from '../shared/HeadingText';
import { usePages, useSite } from '../../contexts/SiteContext';
import { shopLabels } from '../../data/shopLabels';
import { instagramHandle } from '../../core/format/text';
import { fill } from '../../utils/copy';
import { safeUrl } from '../../core/rules/safeUrl';
import { quietLink, sectionHeading } from '../../ui/sections';
import { Section } from '../shared/Section';
import { btnSecondaryLg } from '../../ui/styles';

/**
 * Het slot van het journal, op blush (de footer is klei, dus hier geen klei): de podcast met
 * onderwerpen en afleveringen, elk met een eigen link (leeg = de link naar de show). Daaronder
 * één rustige regel naar Instagram met de handle uit Instellingen; geen feed (snelheid, privacy).
 */
export function JournalPodcast() {
  const pages = usePages();
  // De handle uit de actuele instellingen (Instellingen › Salon & contact), net als de footer.
  const {
    salon
  } = useSite();
  const copy = pages.journal;
  // Links komen uit de paginateksten: alleen https (core/rules/safeUrl.ts).
  const showUrl = safeUrl(copy.podcast.url);
  const handle = salon.instagram ? instagramHandle(salon.instagram, salon.name) : '';
  return <Section tone="blush" labelledBy="podcast-title" contained={false} data-id="element-7909" data-mp="3u19gjclpg29h">
      <div className="mx-auto grid max-w-content gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-7910" data-mp="3p4j36x1abkrr">
        <div className="lg:col-span-5" data-id="element-7911" data-mp="3k4wpynlgi4iy">
          {copy.podcast.eyebrow && <p className="text-[14px] text-ink" data-id="element-7912" data-mp="3fdwsnh8fdkfx">{fill(copy.podcast.eyebrow)}</p>}
          <h2 id="podcast-title" className={`mt-5 ${sectionHeading} text-ink`} data-id="element-7913" data-mp="3fbtsi4dk6axh">
            <HeadingText heading={copy.podcast.heading} data-id="element-7914" data-mp="3afd0gz38ncf4" />
          </h2>
          {copy.podcast.body && <p className="mt-7 max-w-md text-[17px] leading-relaxed text-ink" data-id="element-7915" data-mp="36et623a1umwo">{fill(copy.podcast.body)}</p>}
          {copy.podcast.topics.length > 0 && <ul className="mt-7 flex flex-wrap gap-2" aria-label="Onderwerpen" data-id="element-7916" data-mp="2wfbzljmz44zn">
              {copy.podcast.topics.map(t => <li key={t} className="rounded-full border border-ink/20 px-3 py-1 text-[13px] text-ink" data-id="element-7917" data-mp="2mqn9kh0fp7ez">
                  {t}
                </li>)}
            </ul>}
          {copy.podcast.cta && showUrl && <a href={showUrl} target="_blank" rel="noreferrer" className={`mt-9 ${btnSecondaryLg}`} data-id="element-7918" data-mp="2cvd4fkeqojwz">
              {fill(copy.podcast.cta)}
              <span className="sr-only" data-id="element-7919" data-mp="35ho5sxa66ouu"> {shopLabels.newTab}</span>
              <ArrowUpRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7920" data-mp="35kafp3kj9gg8" />
            </a>}
        </div>

        <div className="lg:col-span-7" data-id="element-7921" data-mp="2vswix3jyuuqs">
          {copy.episodes.length > 0 && <ul className="divide-y divide-ink/15 border-y border-ink/15" aria-label="Afleveringen" data-id="element-7922" data-mp="30qy2dplb0w2s">
              {copy.episodes.map(e => {
            const href = safeUrl(e.url) ?? showUrl ?? undefined;
            return <li key={e.number} data-id="element-7923" data-mp="3t8abihwpbx1o">
                    <a href={href} target="_blank" rel="noreferrer" className="group grid w-full gap-3 py-7 text-left sm:grid-cols-12 sm:items-center sm:gap-6" data-id="element-7924" data-mp="3t9d18miaftg0">
                      <span className="flex items-center gap-4 sm:col-span-5" data-id="element-7925" data-mp="1csxaqc9d96i5">
                        <span aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-ink/25 text-ink transition-colors duration-150 ease-soft group-hover:border-ink group-hover:bg-ink group-hover:text-shell" data-id="element-7926" data-mp="1rhb9oc3nwatf">
                          <PlayIcon className="h-3 w-3 fill-current" strokeWidth={1.5} data-id="element-7927" data-mp="twp351svsjjo" />
                        </span>
                        <span data-id="element-7928" data-mp="13ss3q3834a4i">
                          <span className="sr-only" data-id="element-7929" data-mp="13sikcyhjp17s">Beluister </span>
                          <span className="block type-title text-ink" data-id="element-7930" data-mp="2j9ipxbf99l7a">{e.title}</span>
                          <span className="mt-0.5 block text-[13px] text-muted" data-id="element-7931" data-mp="2jbdf51xk7zh6">
                            Afl. {e.number} · {e.duration}
                            {e.date ? ` · ${e.date}` : ''}
                          </span>
                        </span>
                      </span>
                      <span className="text-[15px] leading-relaxed text-muted sm:col-span-7" data-id="element-7932" data-mp="3els0s89ihoi4">{e.summary}</span>
                      <span className="sr-only" data-id="element-7933" data-mp="3jl4wliid25wu">{shopLabels.newTab}</span>
                    </a>
                  </li>;
          })}
            </ul>}

          {handle && copy.podcast.instagramLine && <p className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-1 text-[15px] text-muted" data-id="element-7934" data-mp="1cdugonqqpcy">
              {fill(copy.podcast.instagramLine)}
              <a href={salon.instagram} target="_blank" rel="noreferrer" className={`${quietLink} text-ink`} data-id="element-7935" data-mp="65yusnct3vfb">
                @{handle}
                <span className="sr-only" data-id="element-7936" data-mp="g2lf9b180kgt"> op Instagram {shopLabels.newTab}</span>
                <ArrowUpRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-7937" data-mp="l165chmrdnrj" />
              </a>
            </p>}
        </div>
      </div>
    </Section>;
}